// src/utils/noms.ts — SCE Issues
// Comment on nomme quelqu'un dans l'appli. Pur, testable.
//
// SharePoint et Azure AD renvoient le nom « convivial » au format de
// l'annuaire Sartorius : « Delavault, Guillaume ». Affiché tel quel, l'accueil
// disait « Bonjour Delavault, » (retour de Guillaume du 26/09). Règle :
//   - nom complet : « Prénom Nom » — `nomComplet` ;
//   - pour simplifier (salutation) : le prénom seul — `prenom`.
// Le nom est remis dans l'ordre À LA SOURCE (compte connecté, déclarant,
// auteur d'un événement, abonnés, présence) : tout ce qui suit — affichage,
// export, initiales, « c'est moi ? » — reçoit déjà « Prénom Nom ».
//
// Formes reconnues :
//   « Delavault, Guillaume »            → Guillaume Delavault
//   « DELAVAULT, Guillaume »            → Guillaume Delavault (nom en capitales)
//   « Dupont, Marie Claire »            → Marie Claire Dupont (prénom composé)
//   « guillaume.delavault1@sartorius.com » → Guillaume Delavault (adresse seule)
//   « lucile.goy1 », « jean-philippe.geny » → Lucile Goy, Jean-Philippe Geny
//                                   (identifiant : chef de projet Windchill)
//   « Guillaume Delavault »             → inchangé (déjà dans l'ordre)
//
// Externes (20/10, élargi le 23/10) : leur annuaire écrit « NOM Prénom » sans
// virgule (« LOUIS Charles » : prénom Charles). Réservé d'abord à
// `@vinci-energies.net`, mais Charles Louis est chez Actemium
// (`@actemium.com`), et Spie, TEEI… font de même. Règle : hors adresse
// `@sartorius.com` (externe, invité `…#EXT#`, ou adresse inconnue), les mots EN
// CAPITALES de tête sont le nom. Un compte Sartorius écrit toujours « Nom,
// Prénom » : jamais concerné. Déjà remis dans l'ordre (« Charles Louis ») :
// inchangé, la règle peut passer deux fois.

/** « DELAVAULT » → « Delavault » ; « jean-pierre » → « Jean-Pierre ». Un nom en casse mixte reste tel quel. */
function capitaliser(mot: string): string {
  return mot
    .split('-')
    .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : p))
    .join('-')
}

function nomDeFamille(nom: string): string {
  const t = nom.trim()
  // Capitales seulement : « de la Tour » ou « McDonald » sont laissés tels quels.
  return t === t.toUpperCase() && /[A-ZÀ-Ý]/.test(t) ? t.split(/\s+/).map(capitaliser).join(' ') : t
}

interface Parties {
  prenom: string
  nom: string
}

/** Compte interne : « Nom, Prénom » de l'annuaire Sartorius. Un invité (#EXT#) n'en est pas un. */
function adresseSartorius(adresse: string | null | undefined): boolean {
  const a = (adresse ?? '').trim()
  return /@sartorius\.com$/i.test(a) && !/#EXT#/i.test(a)
}

function enCapitales(mot: string): boolean {
  return mot === mot.toUpperCase() && /[A-ZÀ-Ý]/.test(mot)
}

function decouper(brut: string | null | undefined, adresse?: string | null): Parties | null {
  const t = (brut ?? '').trim().replace(/\s+/g, ' ')
  if (!t) return null

  // « NOM Prénom » d'un externe : les mots en capitales de tête = le nom.
  if (!adresseSartorius(adresse) && !t.includes(',')) {
    const mots = t.split(' ')
    const n = mots.findIndex((m) => !enCapitales(m))
    if (n > 0) return { prenom: mots.slice(n).join(' '), nom: nomDeFamille(mots.slice(0, n).join(' ')) }
  }

  // « Nom, Prénom »
  const virgule = /^([^,]+),\s*(.+)$/.exec(t)
  if (virgule) return { prenom: (virgule[2] ?? '').trim(), nom: nomDeFamille(virgule[1] ?? '') }

  // Adresse seule « guillaume.delavault1@… », ou identifiant « lucile.goy1 »
  // (Owner de Windchill) : le chiffre des comptes homonymes est retiré.
  if (!t.includes(' ') && (t.includes('@') || /^[\p{L}-]+[._][\p{L}._-]*\d*$/u.test(t))) {
    const morceaux = (t.split('@')[0] ?? '').replace(/\d+$/, '').split(/[._]+/).filter(Boolean).map(capitaliser)
    if (morceaux.length === 0) return { prenom: t, nom: '' }
    return { prenom: morceaux[0] ?? '', nom: morceaux.slice(1).join(' ') }
  }

  // Déjà « Prénom Nom » (ou un seul mot)
  const [premier = '', ...reste] = t.split(' ')
  return { prenom: premier, nom: reste.join(' ') }
}

/**
 * « Prénom Nom », pour tout affichage qui désigne quelqu'un sans ambiguïté.
 * `adresse` (UPN ou mail) : à passer quand on l'a, elle décide des externes Vinci.
 */
export function nomComplet(brut: string | null | undefined, adresse?: string | null): string {
  const p = decouper(brut, adresse)
  if (!p) return ''
  return [p.prenom, p.nom].filter(Boolean).join(' ')
}

/** Le prénom seul, quand on simplifie (« Bonjour Guillaume »). */
export function prenom(brut: string | null | undefined, adresse?: string | null): string {
  return decouper(brut, adresse)?.prenom ?? ''
}

/** Initiales « GD » (prénom puis nom) pour les pastilles. */
export function initialesDe(brut: string | null | undefined): string {
  const p = decouper(brut)
  if (!p) return '?'
  const nom = p.nom.split(' ').filter(Boolean)
  return `${p.prenom.charAt(0)}${(nom[nom.length - 1] ?? '').charAt(0)}`.toUpperCase() || '?'
}

/**
 * Trigramme de l'usine, celui des feuilles « Montage et Tests » : initiale du
 * prénom + deux premières lettres du nom (Thibaut Hacquin → THA). Prénom
 * composé : une initiale par partie + celle du nom (Jean-Philippe Geny → JPG).
 * Sans accents, en capitales ; vide si le nom est inconnu.
 */
export function trigrammeDe(brut: string | null | undefined): string {
  const p = decouper(brut)
  if (!p) return ''
  const sansAccents = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '')
  const nom = sansAccents(p.nom.split(' ').filter(Boolean).pop() ?? '').replace(/[^A-Za-z]/g, '')
  const parties = sansAccents(p.prenom).split('-').filter(Boolean)
  const tri = parties.length > 1
    ? parties.map((x) => x.charAt(0)).join('') + nom.charAt(0)
    : (parties[0] ?? '').charAt(0) + nom.slice(0, 2)
  return tri.toUpperCase()
}
